/**
 * HmProducts — kartoteka towarów czytana z Symfonii.
 *
 * To NIE jest lokalna kartoteka Towary z WFNEW (ta żyje w naszej bazie i obsługuje picker na
 * dokumentach) — tu oglądamy kartotekę Symfonii, tak jak widzi ją Handel.
 *
 * Jedyna kartoteka bez dat: towary nie mają zakresu czasowego, więc lewy panel dat jest wyłączony,
 * a całość pobiera się jednym zapytaniem. Grupujemy po rodzaju towaru (towar/usługa/komplet).
 *
 * PUŁAPKA KONTRAKTU: stawka VAT przychodzi tu jako zwykły tekst („23%"), a nie jako obiekt ze
 * słownika — inaczej niż na pozycjach dokumentów sprzedaży.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { HmProductListItem } from '@saas/shared';
import { Package } from 'lucide-react';

import type { ColumnDefinition } from '@/components/configurable-table';
import { HmListShell, rangeFor } from '@/components/handel-magazyn/HmListShell';
import { ReportsMenuButton } from '@/components/handel-magazyn/ReportsMenuButton';
import {
  defaultProductParams,
  PRODUCT_REPORT_KINDS,
  productReportPath,
  productReportTitleKey,
  toProductSearchParams,
} from '@/lib/handel-magazyn/product-reports-api';

export function HmProducts() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = useMemo<ColumnDefinition<HmProductListItem>[]>(
    () => [
      {
        id: 'code',
        header: t('tenant.handelMagazyn.products.columns.code'),
        accessor: (row) => row.code,
        defaultWidth: 150,
        renderCell: (_v: unknown, row: HmProductListItem) => (
          <span className="font-mono text-xs text-text-primary">{row.code}</span>
        ),
      },
      {
        id: 'name',
        header: t('tenant.handelMagazyn.products.columns.name'),
        accessor: (row) => row.name,
        defaultWidth: 300,
      },
      {
        id: 'typeLabel',
        header: t('tenant.handelMagazyn.products.columns.type'),
        accessor: (row) => row.typeLabel,
        defaultWidth: 110,
      },
      {
        id: 'unit',
        header: t('tenant.handelMagazyn.products.columns.unit'),
        accessor: (row) => row.unit ?? '—',
        defaultWidth: 80,
      },
      {
        id: 'vatRate',
        header: t('tenant.handelMagazyn.columns.vat'),
        accessor: (row) => row.vatRate ?? '—',
        defaultWidth: 90,
      },
      {
        id: 'active',
        header: t('tenant.handelMagazyn.products.columns.active'),
        accessor: (row) =>
          row.active ? t('tenant.handelMagazyn.detail.yes') : t('tenant.handelMagazyn.detail.no'),
        defaultWidth: 100,
        renderCell: (_v: unknown, row: HmProductListItem) => (
          <span
            className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
              row.active ? 'bg-emerald-100 text-emerald-800' : 'bg-bg-tertiary text-text-secondary'
            }`}
          >
            {row.active
              ? t('tenant.handelMagazyn.products.activeYes')
              : t('tenant.handelMagazyn.products.activeNo')}
          </span>
        ),
      },
      {
        id: 'barcode',
        header: t('tenant.handelMagazyn.products.columns.barcode'),
        accessor: (row) => row.barcode ?? '—',
        defaultWidth: 150,
      },
      {
        id: 'cn',
        header: t('tenant.handelMagazyn.products.columns.cn'),
        accessor: (row) => row.cn ?? '—',
        defaultWidth: 110,
      },
      {
        id: 'pkwiu',
        header: t('tenant.handelMagazyn.products.columns.pkwiu'),
        accessor: (row) => row.pkwiu ?? '—',
        defaultWidth: 110,
      },
    ],
    [t],
  );

  /**
   * Zestawienia towarowe — osiem pozycji jak w Symfonii. Otwierają się od razu: parametrem jest
   * co najwyżej zakres dat, a ten zmienia się wygodniej na gotowym raporcie niż w oknie przed nim.
   * „Wykonaj dla zaznaczonych" zawęża raport do zaznaczonych towarów.
   */
  function reportItems(ctx: { selected: HmProductListItem[] }) {
    const ids = ctx.selected.map((row) => row.id);
    const month = rangeFor('thisMonth');
    const open = (kind: (typeof PRODUCT_REPORT_KINDS)[number], onlySelected: boolean) => () => {
      const params = {
        ...defaultProductParams(kind, month.from, month.to),
        preset: 'thisMonth' as const,
        ids: onlySelected ? ids : [],
      };
      navigate(`${productReportPath(kind)}?${toProductSearchParams(params)}`);
    };

    return [
      {
        key: 'selected',
        label: t('tenant.handelMagazyn.reports.forSelected', { count: ids.length }),
        onSelect: ids.length > 0 ? open('stany-i-rezerwacje', true) : undefined,
      },
      ...PRODUCT_REPORT_KINDS.map((kind) => ({
        key: kind,
        label: t(productReportTitleKey(kind)),
        onSelect: open(kind, false),
      })),
    ];
  }

  return (
    <HmListShell<HmProductListItem>
      title={t('tenant.handelMagazyn.products.title')}
      resource="products"
      queryKey="products"
      tableId="hm_products"
      testIdPrefix="hm-products"
      columns={columns}
      getRowId={(row) => String(row.id)}
      selectable
      onNew={() => navigate('/handel-magazyn/towary/nowy')}
      renderReports={(ctx) => (
        <ReportsMenuButton items={reportItems(ctx)} testIdPrefix="hm-product-reports" />
      )}
      onRowClick={(row) => navigate(`/handel-magazyn/towary/${row.id}`)}
      emptyIcon={Package}
      showDateFilter={false}
      groupBy={{ label: t('tenant.handelMagazyn.products.kinds'), of: (row) => row.typeLabel }}
    />
  );
}

export default HmProducts;
